import {
  FullScreenSliderSection,
  HomeHero,
  Layout,
  MuseumProgram,
  ContentNotFound,
  SsmFriendlyCardAdvantages,
  SsmShop,
  SsmInstagram,
  DiscoverSsm,
  SelectionsFromWork,
} from '@common'
import { ISSRPageData } from '@interfaces'
import { PageService } from '@services'
import { createNavLink } from '@utils'
import {
  DISCOVER_ART_DEMO_SLIDER_ID,
  DISCOVER_ART_DEMO_SLIDER_IMAGE,
  DISCOVER_ART_DEMO_SLIDER_MOBILE_IMAGE,
} from '@lib/const/discover-art-demo-slider.const'
import { observer } from 'mobx-react-lite'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

const HomeScreen = observer(
  ({ pageData, global: { navigation } }: ISSRPageData) => {
    const { t } = useTranslation()
    const router = useRouter()
    const discoverArtDemoSlider = [
      {
        id: DISCOVER_ART_DEMO_SLIDER_ID,
        typeLabel: t('home:discoverArt.arisingSlide.typeLabel'),
        date: t('home:discoverArt.arisingSlide.date'),
        title: t('home:discoverArt.arisingSlide.title'),
        subtitle: '',
        description: t('home:discoverArt.arisingSlide.overlayDescription'),
        overlayTitle: t('home:discoverArt.arisingSlide.overlayTitle'),
        shortDescription: t('home:discoverArt.arisingSlide.shortDescription'),
        imgUrl: DISCOVER_ART_DEMO_SLIDER_IMAGE,
        mobileImgUrl: DISCOVER_ART_DEMO_SLIDER_MOBILE_IMAGE,
        url: createNavLink('arisingACall', router.locale),
      },
    ]
    if (pageData.error) {
      return (
        <Layout
          title={t('common:ssmPageTitle')}
          navigation={navigation}
          path={router.asPath}
        >
          <ContentNotFound infoType="serverSideError" />
        </Layout>
      )
    }
    return (
      <Layout
        title={t('common:ssmPageTitle')}
        navigation={navigation}
        path={router.asPath}
        isHomePage={true}
      >
        <HomeHero swiperCollection={pageData.data.headlines} />
        {pageData.data?.currentMuseumProgram.length && (
          <MuseumProgram tabData={pageData.data.currentMuseumProgram} />
        )}

        <FullScreenSliderSection
          subTitle={t('home:discoverArt.subTitle')}
          title={t('home:discoverArt.title')}
          description={t('home:discoverArt.description')}
          sliderCollection={discoverArtDemoSlider}
          bgImageUrl={DISCOVER_ART_DEMO_SLIDER_IMAGE}
        />

        {pageData.data?.exploreSSM.length > 0 && (
          <DiscoverSsm
            subTitle={t('common:discoverSsm.title1')}
            title={t('common:discoverSsm.title2')}
            description={t('common:discoverSsm.description')}
            sliderCollection={pageData.data?.exploreSSM}
          />
        )}

        {pageData.data?.selectionsFromCollections.length > 0 && (
          <SelectionsFromWork
            data={pageData.data.selectionsFromCollections}
            sectionTitle={t('common:selectionsFromCollections.desc')}
            sectionSubTitle={t('common:selectionsFromCollections.title')}
            footerButtonLabel={t(
              'common:selectionsFromCollections.buttonLabel'
            )}
            footerButtonOnPress={() =>
              router.push(createNavLink('collections', router.locale))
            }
            containerStyle={{
              pt: { base: 'padding.primary2', lg: 'padding.primary4' },
            }}
          />
        )}

        <SsmFriendlyCardAdvantages />
        <SsmShop />

        {pageData.data?.instagramMedia && (
          <SsmInstagram media={pageData.data?.instagramMedia} />
        )}
      </Layout>
    )
  }
)

export default HomeScreen

export async function getStaticProps({ locale }) {
  const pageData = {
    data: null,
    error: null,
  }

  try {
    const collection = await PageService.getHomePage(locale)

    pageData.data = collection
  } catch (err) {
    if (err instanceof Error) {
      pageData.error = { message: err.message }
    }
  }

  return {
    props: { pageData },
    revalidate: 300,
  }
}
